/* Lender · Buyer Underwriting roster (Wave C.3 · iter-101 · Feb 2026).

   Mounted at /lender/buyer-underwriting. Shows every buyer in this
   lender's book with their:
     • Credit grant (limit + active flag + admin overrides)
     • Allianz underwriting verdict + policy limits + utilization
     • Lender-specific utilization (open loans + outstanding)
     • Sales signal (lifetime principal funded)

   Click a row → drilldown dialog with the per-buyer forecast
   (1w/1m/1q/1y windows + by-terms breakdown).
*/
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Shield, RefreshCw, Search, TrendingUp, AlertTriangle, ShieldCheck, BarChart3, Calendar,
  Snowflake,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import TradeScoreBadge from "../../components/TradeScoreBadge";

const fmtMoney = (n) => n == null ? "—" : new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(n);
const fmtMoneyP = (n) => n == null ? "—" : new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
}).format(n);

function VerdictPill({ decision }) {
  const map = {
    approved: { tone: "emerald", label: "Approved" },
    declined: { tone: "rose",    label: "Declined" },
    pending:  { tone: "amber",   label: "Pending" },
    countered:{ tone: "sky",     label: "Countered" },
  };
  const meta = map[decision] || { tone: "slate", label: decision || "—" };
  return (
    <span data-testid={`verdict-${decision || "none"}`}
      className={`px-2 py-0.5 rounded-full text-[10px] bg-${meta.tone}-50 text-${meta.tone}-700 border border-${meta.tone}-200`}>
      {meta.label}
    </span>
  );
}

function UtilizationBar({ used, total }) {
  if (!total) return <span className="text-[11px] text-slate-400">—</span>;
  const pct = Math.min(100, Math.max(0, (used / total) * 100));
  const tone = pct > 90 ? "rose" : pct > 70 ? "amber" : "emerald";
  return (
    <div className="w-full">
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full bg-${tone}-500 transition-all`} style={{ width: `${pct}%` }}/>
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5 tabular-nums">{Math.round(pct)}% used</div>
    </div>
  );
}

export default function LenderBuyerUnderwriting() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/lender/buyer-underwriting");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not load roster");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      (r.buyer_company || "").toLowerCase().includes(needle) ||
      (r.buyer_email || "").toLowerCase().includes(needle) ||
      (r.buyer_id || "").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const summary = useMemo(() => {
    const total = rows.length;
    const approved = rows.filter((r) => r.allianz_policy?.allianz_decision === "approved").length;
    const declined = rows.filter((r) => r.allianz_policy?.allianz_decision === "declined").length;
    const outstanding = rows.reduce((s, r) => s + (r.lender_utilization?.outstanding_usd || 0), 0);
    return { total, approved, declined, outstanding };
  }, [rows]);

  return (
    <div data-testid="lender-buyer-underwriting" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Shield className="w-6 h-6 text-violet-500"/> Buyer underwriting
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Allianz's verdict + your utilization + sales-volume signal for every buyer you've funded.
            This is how you see <em>why</em> a credit limit is what it is.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="uw-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="uw-kpis">
        <KpiSm label="Total buyers"     value={summary.total}                        tone="slate"   icon={Shield}/>
        <KpiSm label="Allianz approved" value={summary.approved}                     tone="emerald" icon={ShieldCheck}/>
        <KpiSm label="Allianz declined" value={summary.declined}                     tone="rose"    icon={AlertTriangle}/>
        <KpiSm label="Outstanding $"    value={fmtMoney(summary.outstanding)}        tone="violet"  icon={TrendingUp}/>
      </div>

      <div className="rounded-2xl border bg-white px-4 py-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input data-testid="uw-search" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 bg-slate-50" placeholder="Search company / email / buyer id…"/>
        </div>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Credit grant</div>
          <div className="col-span-2">Allianz verdict</div>
          <div className="col-span-2">Allianz utilization</div>
          <div className="col-span-1 text-right">Open loans</div>
          <div className="col-span-2 text-right">Outstanding</div>
        </div>
        <div data-testid="uw-table">
          {!loading && visible.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="uw-empty">No buyers in your book yet.</div>
          )}
          {visible.map((r) => (
            <button key={r.buyer_id} data-testid={`uw-row-${r.buyer_id}`} onClick={() => setOpen(r)}
              className="w-full text-left grid grid-cols-12 px-4 py-2.5 border-b text-sm hover:bg-slate-50/70">
              <div className="col-span-3 min-w-0">
                <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                  {r.buyer_company || "—"}
                  {r.suspended && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Suspended</span>}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{r.buyer_email}</div>
              </div>
              <div className="col-span-2">
                <div className="tabular-nums text-slate-900">{fmtMoney(r.credit_grant?.limit_usd)}</div>
                <div className="text-[10px] text-slate-500">
                  {r.credit_grant?.active ? "Active" : "Inactive"}
                  {r.credit_grant?.admin_override != null && (
                    <> · override {fmtMoney(r.credit_grant.admin_override)}</>
                  )}
                </div>
              </div>
              <div className="col-span-2 text-xs">
                <VerdictPill decision={r.allianz_policy?.allianz_decision}/>
                {r.allianz_policy?.total_limit_usd ? (
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {fmtMoney(r.allianz_policy.total_limit_usd)} cap
                  </div>
                ) : null}
              </div>
              <div className="col-span-2">
                <UtilizationBar
                  used={r.allianz_policy?.used_usd || 0}
                  total={r.allianz_policy?.total_limit_usd || 0}
                />
              </div>
              <div className="col-span-1 text-right text-sm tabular-nums">{r.lender_utilization?.open_loans || 0}</div>
              <div className="col-span-2 text-right text-sm tabular-nums">{fmtMoneyP(r.lender_utilization?.outstanding_usd)}</div>
            </button>
          ))}
        </div>
      </div>

      <BuyerForecastDialog buyer={open} onClose={() => setOpen(null)} onChanged={load}/>
    </div>
  );
}

function KpiSm({ label, value, tone = "slate", icon: Icon }) {
  return (
    <div className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3 flex items-center justify-between`}>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-0.5">{value}</div>
      </div>
      {Icon && <Icon className={`w-5 h-5 text-${tone}-500`}/>}
    </div>
  );
}

function BuyerForecastDialog({ buyer, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [frozen, setFrozen] = useState(null);   // active freeze metadata
  const [freezeBusy, setFreezeBusy] = useState(false);

  useEffect(() => {
    if (!buyer) { setData(null); setFrozen(null); return; }
    setLoading(true);
    api.get(`/partner/lender/buyers/${buyer.buyer_id}/forecast`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // Also load the freeze status (best-effort)
    api.get(`/partner/lender/buyer-freezes`)
      .then((r) => {
        const match = (r.data?.rows || []).find((f) => f.buyer_id === buyer.buyer_id);
        setFrozen(match || null);
      })
      .catch(() => setFrozen(null));
  }, [buyer?.buyer_id]);

  if (!buyer) return null;

  const unfreeze = async () => {
    const reason = window.prompt("Reason for lifting the freeze (≥3 chars):");
    if (!reason || reason.trim().length < 3) return;
    setFreezeBusy(true);
    try {
      await api.post(`/partner/lender/buyers/${buyer.buyer_id}/unfreeze`, { reason });
      toast.success("Buyer unfrozen");
      setFrozen(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not unfreeze");
    } finally { setFreezeBusy(false); }
  };

  return (
    <Dialog open={!!buyer} onOpenChange={onClose}>
      <DialogContent data-testid="uw-buyer-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <BarChart3 className="w-4 h-4 text-violet-500"/>
            {buyer.buyer_company || buyer.buyer_email}
            {frozen && (
              <span data-testid="uw-frozen-badge"
                className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                ❄ Frozen
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Per-buyer forecast across all loans this lender has funded to this buyer.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-center text-sm text-slate-500 py-8">Loading…</div>
        )}
        {data && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-slate-50 px-4 py-3 text-sm grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
              <div><span className="text-[10px] uppercase text-slate-500">Buyer ID</span><div className="font-mono text-xs">{data.buyer.id?.slice(0, 12)}…</div></div>
              <div><span className="text-[10px] uppercase text-slate-500">Credit limit</span><div className="tabular-nums">{fmtMoney(data.buyer.credit_grant?.limit_usd)}</div></div>
              <div><span className="text-[10px] uppercase text-slate-500">Trade Score</span><div data-testid="uw-buyer-trade-score">{data.buyer.id ? <TradeScoreBadge userId={data.buyer.id} size="sm"/> : "—"}</div></div>
              <div><span className="text-[10px] uppercase text-slate-500">Open loans</span><div className="tabular-nums">{data.open_loan_count}</div></div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Expected inflows from this buyer</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="uw-buyer-forecast-windows">
                <ForecastTile label="This week"     value={data.expected_inflows_usd?.["1w"]}    icon={Calendar} tone="emerald"/>
                <ForecastTile label="This month"    value={data.expected_inflows_usd?.["1m"]}    icon={Calendar} tone="emerald"/>
                <ForecastTile label="This quarter"  value={data.expected_inflows_usd?.["1q"]}    icon={Calendar} tone="violet"/>
                <ForecastTile label="This year"     value={data.expected_inflows_usd?.["1y"]}    icon={Calendar} tone="sky"/>
                <ForecastTile label="Past due"      value={data.expected_inflows_usd?.past_due_collected_today} icon={AlertTriangle} tone="rose"/>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">By terms</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {Object.entries(data.by_terms_usd || {}).length === 0 && (
                  <div className="text-xs text-slate-500">No open loans.</div>
                )}
                {Object.entries(data.by_terms_usd || {}).map(([k, v]) => (
                  <div key={k} className="rounded-md border bg-white px-3 py-2 text-sm">
                    <div className="text-[10px] uppercase text-slate-500">{k.toUpperCase()}</div>
                    <div className="tabular-nums">{fmtMoneyP(v)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Inflow timeline (next 60 days)</div>
              <div className="p-3 bg-white border rounded-lg">
                {(!data.timeline || data.timeline.length === 0) ? (
                  <div className="text-xs text-slate-500 text-center py-4">No upcoming inflows.</div>
                ) : (() => {
                  const max = Math.max(...data.timeline.map((d) => d.amount));
                  return (
                    <div className="flex items-end gap-px h-20">
                      {data.timeline.slice(0, 60).map((d) => (
                        <div key={d.date} title={`${d.date}: ${fmtMoneyP(d.amount)}`}
                          className="flex-1 bg-violet-500 hover:bg-violet-600 transition-colors"
                          style={{ height: `${(d.amount / max) * 100}%`, minHeight: "2px" }}/>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {frozen ? (
              <Button variant="outline" size="sm" disabled={freezeBusy} onClick={unfreeze} data-testid="uw-unfreeze-btn">
                <Snowflake className="w-4 h-4 mr-1.5"/> Unfreeze buyer
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setFreezeOpen(true)} data-testid="uw-freeze-btn">
                <Snowflake className="w-4 h-4 mr-1.5"/> Freeze buyer
              </Button>
            )}
          </div>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>

        <FreezeBuyerDialog
          buyer={buyer}
          open={freezeOpen}
          onClose={() => setFreezeOpen(false)}
          onFrozen={(meta) => { setFreezeOpen(false); setFrozen(meta); onChanged?.(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function FreezeBuyerDialog({ buyer, open, onClose, onFrozen }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/partner/lender/buyers/${buyer.buyer_id}/freeze`, {
        reason: reason.trim(),
      });
      toast.success("Buyer frozen · new loans blocked");
      onFrozen?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not freeze");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="uw-freeze-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Snowflake className="w-4 h-4 text-sky-600"/> Freeze {buyer?.buyer_company || buyer?.buyer_email}?
          </DialogTitle>
          <DialogDescription>
            Blocks NEW loans from this buyer under your lender. <strong>Existing loans keep running normally.</strong> The buyer is notified by email.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Reason (≥3 chars · audit-logged)</Label>
            <Textarea
              data-testid="uw-freeze-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. 40% drop in monthly volume + 2 consecutive NSF events."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="uw-freeze-submit">
            {busy ? "Freezing…" : "Freeze buyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ForecastTile({ label, value, icon: Icon, tone }) {
  return (
    <div className={`rounded-md border bg-${tone}-50 border-${tone}-200 px-3 py-2`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-3.5 h-3.5 text-${tone}-500`}/>}
      </div>
      <div className="text-base font-semibold tabular-nums text-slate-900 mt-0.5">{fmtMoney(value)}</div>
    </div>
  );
}
